import { calculateDistanceBetweenCoordinates } from 'shared/map/lib/utils';
import { any2unixtime, binarySearchByField, escape_html, prettyTimeInterval, } from 'utils';
import memoizeOne from 'memoize-one';
import { JobType } from 'console/worker/types';
import { COORD_PROVIDER_NETWORK, } from 'entities/device.map.track/model/types';
/**
 * @function convertPoints - Конвертируем GPS-точки из сырых в теми, которыми будем пользоваться
 * @param gps_raw_data - массив сырых GPS-точек
 * @return PointFromGPS[]
 */
export function convertPoints(gps_raw_data) {
    if (gps_raw_data == null) {
        return [];
    }
    return gps_raw_data.map((gps_raw) => ({
        time: new Date(gps_raw[0] * 1e3),
        loc: [gps_raw[1], gps_raw[2]],
        speed: gps_raw[3],
        prov: gps_raw[4],
        single: gps_raw[5],
        accuracy: gps_raw[6],
    }));
}
function isBadGPS(x, y) {
    const abs_x = Math.abs(x);
    const abs_y = Math.abs(y);
    return (abs_x === 90 ||
        abs_x === 180 ||
        abs_x === 0 ||
        abs_y === 90 ||
        abs_y === 180 ||
        abs_y === 0);
}
/**
 * @function filterGpsJumps - фильтруем прыжки, убираем их из массива
 * @param gps - массив GPS-точек
 * @return PointFromGPS[]
 */
export function filterGpsJumps(gps) {
    // original algorithm:
    // https://git.microline.ru/autoscan/LibraryAutoScan/blob/1ea37b472152074df34e8240f7801eb9cbeb1a7a/CarBase.cpp#L434
    if (gps.length < 3) {
        return gps;
    }
    if (gps[gps.length - 1].time.getTime() - gps[0].time.getTime() < 3 * 60e3) {
        return gps;
    }
    gps = gps.slice();
    let time_in_chunk = 0;
    let last_i_before_jump = null;
    let jump = false;
    let stable = false;
    let i = 0;
    while (i < gps.length - 1) {
        i++;
        const prev = gps[i - 1];
        const cur = gps[i];
        //Если это ТОЧНО плохая точка, то удаляем
        if (isBadGPS(cur.loc[0], cur.loc[1])) {
            gps.splice(i, 1);
        }
        if (jump && last_i_before_jump != null) {
            const d = calculateDistanceBetweenCoordinates(gps[last_i_before_jump].loc, cur.loc);
            if (10e3 < d && d < 40000e3 && time_in_chunk > 5 * 60e3) {
                if (!stable) {
                    gps.splice(0, last_i_before_jump + 1);
                    i -= last_i_before_jump + 1;
                }
                stable = true;
                jump = false;
            }
            else if (d < 10e3) {
                gps.splice(last_i_before_jump + 1, i - last_i_before_jump - 1);
                i = last_i_before_jump;
                jump = false;
            }
            else {
                time_in_chunk += cur.time.getTime() - prev.time.getTime();
            }
        }
        else {
            if (calculateDistanceBetweenCoordinates(prev.loc, cur.loc) > 10e3) {
                if (time_in_chunk > 2 * 60e3) {
                    stable = true;
                }
                jump = true;
                time_in_chunk = 0;
                last_i_before_jump = i - 1;
            }
            else {
                time_in_chunk += cur.time.getTime() - prev.time.getTime();
            }
        }
    }
    return gps;
}
/**
 * @function markTeleports - отмечаем телепорты
 * @param points - массив GPS-точек
 * @param events - массив сырых событий
 * @return PointFromGPS[]
 */
export function markTeleports(points, events) {
    if (events == null || events.length == 0) {
        return points;
    }
    const result = [];
    let event_i = 0;
    for (const point of points) {
        let gps_lost = false;
        const unixtime = point.time.getTime() / 1e3;
        while (event_i < events.length && events[event_i][1] < unixtime) {
            const event_type = events[event_i][2];
            if (['GPSLost', 'GPSFound'].includes(event_type)) {
                gps_lost = true;
            }
            event_i++;
        }
        if (gps_lost) {
            result.push({ ...point, teleport: true });
        }
        else {
            result.push(point);
        }
    }
    return result;
}
/**
 * @function zontTrackingIcon - какую иконку сделаем
 * @param event - событие из пакетов
 * @return IconFromTrackProcessor | null
 */
export function zontTrackingIcon(event) {
    const [, time, type, x, y, duration, data] = event;
    if (!['Stop', 'Landmark'].includes(type)) {
        return null;
    }
    if (x == null || y == null) {
        return null;
    }
    function choseIcon() {
        if (type == 'Stop') {
            return {
                icon_class: 'ui-icon-map-stop',
                title: 'Остановка',
                details: `Продолжительность: ${prettyTimeInterval(duration != null ? duration : 0, {
                    seconds: false,
                })}`,
            };
        }
        if (type == 'Landmark') {
            return {
                icon_class: 'ui-icon-map-landmark',
                title: data != null && 'label' in data ? data.label : '',
            };
        }
        return { icon_class: '', title: '' };
    }
    return {
        x,
        y,
        time,
        icon_size: 20,
        ...choseIcon(),
    };
}
/**
 * @function insert_icons - иконки в HTML оборачиваем
 * @param lines - массив из строк
 * @param icons - массив из номеров иконок
 * @return string[]
 */
export function insert_icons(lines, icons) {
    let line_no = 0;
    for (const icon of icons) {
        lines[line_no] = `<span class='autoscan-hint-icon autoscan-hint-icon-${icon}'></span> \
                                  <span class='autoscan-hint-text'>${lines[line_no] ? escape_html(lines[line_no]) : ''}</span>`;
        if ([20, 21].includes(icon)) {
            line_no += 1; // 5;
        }
        else {
            line_no += 1;
        }
    }
    return lines;
}
/**
 * @function ghostLocation - находим местоположение призрака на треке
 * @param gps - массив всех GPS точек
 * @param time - время
 * @return GhostLocation
 */
export function ghostLocation(gps, time) {
    if (gps?.length <= 0 || time == null)
        return null;
    const time_ms = new Date(time * 1e3);
    const bin = binarySearchByField(gps, 'time', time_ms);
    let p1 = gps[bin.l];
    const p2 = gps[bin.r];
    //Если не нашли таких точек
    if (p1 == null && p2 != null) {
        return { loc: p2.loc, prevloc: null };
    }
    else if (p1 != null && p2 == null) {
        return { loc: p1.loc, prevloc: null };
    }
    else if (p1 != null &&
        p2 != null &&
        p2.time.getTime() - p1.time.getTime() <= 5 * 60 * 1e3) {
        const frac = (time_ms.getTime() - p1.time.getTime()) /
            (p2.time.getTime() - p1.time.getTime());
        const loc = [
            p1.loc[0] + (p2.loc[0] - p1.loc[0]) * frac,
            p1.loc[1] + (p2.loc[1] - p1.loc[1]) * frac,
        ];
        if (p1.loc[0] === p2.loc[0] && p1.loc[1] === p2.loc[1]) {
            // We can't calculate direction if points are same
            // Trying to find previous point with different coords
            // Note: this hack should not be applied to pos calculation above
            let i = bin.l - 1;
            for (;;) {
                const tmp = gps[i--];
                if (tmp == null) {
                    break;
                }
                if (tmp.loc[0] !== p2.loc[0] || tmp.loc[1] !== p2.loc[1]) {
                    p1 = tmp;
                    break;
                }
            }
        }
        return { loc: loc, prevloc: p1.loc };
    }
    else {
        return { loc: null, prevloc: null };
    }
}
/**
 * @function iterTripsIndices - генерируем gps интервалы для поездок
 * @param gps - массив всех GPS точек
 * @return Generator<GPSInterval>
 */
function* iterTripsIndices(gps) {
    let interval = null;
    let prev = null;
    for (let i = 0; i < gps.length; i++) {
        const p = gps[i];
        if (p.teleport)
            continue;
        if (prev != null) {
            if (p.time.getTime() - prev.time.getTime() > 5 * 60e3) {
                if (interval != null &&
                    interval.r != null &&
                    interval.r - interval.l >= 2)
                    yield interval;
                interval = null;
            }
        }
        if (interval == null)
            interval = { l: i, r: i };
        interval.r = i + 1;
        prev = p;
    }
    if (interval != null && interval.r != null && interval.r - interval.l >= 2)
        yield interval;
}
// /**
//  * @function generateTrips - генерируем поездки
//  * @param raw_gps - массив сырых GPS точек
//  * @return Trip[]
//  */
// export function generateTrips(raw_gps: GPSRawData[]): Trip[] {
//     let prev_end: Date | null = null;
//     const gps: PointFromGPS[] = convertPoints(raw_gps);
//     return Array.from(iterTripsIndices(gps)).map((indices: GPSInterval) => {
//         const times = [gps[indices.l].time, gps[indices.r - 1].time];
//
//         const stop_time =
//             prev_end != null ? times[0].getTime() - prev_end.getTime() : undefined;
//         prev_end = times[1];
//
//         return {
//             times,
//             locs: [gps[indices.l].loc, gps[indices.r - 1].loc],
//             stop_time,
//         };
//     });
// }
/**
 * @function asyncGenerateTrips - генерируем поездки с подсчётом статистики
 * @param capabilities - совместимость прибора
 * @param gps - массив всех GPS точек
 * @param raw_data - сырые данные
 * @param device - устройство
 * @param ref_calcMapStats - функция для вычисления статистики
 * @param onError - функция ошибки
 * @param api - API
 * @return Promise<AsyncTrip[]>
 */
export async function asyncGenerateTrips(capabilities, raw_data, gps, device, onError, api, ref_calcMapStats) {
    const a_gps = gps;
    if (!(a_gps?.length > 0))
        return Promise.resolve([]);
    let prev_end = null;
    return Array.from(iterTripsIndices(a_gps)).map((indices) => {
        const times = [a_gps[indices.l].time, a_gps[indices.r - 1].time];
        const stop_time = prev_end != null ? times[0].getTime() - prev_end.getTime() : undefined;
        prev_end = times[1];
        return {
            times,
            locs: [a_gps[indices.l].loc, a_gps[indices.r - 1].loc],
            onStats: memoizeOne(() => ref_calcMapStats({
                type: JobType.map_stats,
                capabilities,
                raw_data,
                time: {
                    mintime: any2unixtime(times[0].getTime()),
                    maxtime: any2unixtime(times[1].getTime()),
                },
                gps,
                device,
            }, onError, api)),
            stop_time,
        };
    });
}
/**
 * @function filterGPSPoints - фильтруем GPS-точки
 * @param gps - массив всех GPS точек
 * @param hide_gsm - скрываем точки мобильной сети
 * @param accuracy_limit - точность координат
 * @return PointFromGPS[]
 */
export function filterGPSPoints(gps, hide_gsm, accuracy_limit) {
    return gps
        .filter((item) => !hide_gsm || item.prov !== COORD_PROVIDER_NETWORK)
        .filter((item) => accuracy_limit == null ||
        item.accuracy == null ||
        item.accuracy <= accuracy_limit);
}
